import { useQuery, useQueryClient, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  requestAlarms,
  requestAlarm,
  createAlarmRequest,
  updateAlarmRequest,
  updateAlarmReadmeRequest,
  deleteAlarmRequest,
  ICreateAlarmDTO,
  IUpdateAlarmDTO,
} from '_/api/alarms/alarms.api';
import { notification } from 'antd';

// Query keys
export const ALARMS_QUERY_KEY = (alarmGroupId: string): string[] => ['alarmGroup', alarmGroupId, 'alarms'];
export const ALARM_QUERY_KEY = (alarmId: string): string[] => ['alarm', alarmId];

/**
 * Hook to fetch alarms for an alarm group
 */
export const useAlarms = (alarmGroupId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: ALARMS_QUERY_KEY(alarmGroupId || ''),
    queryFn: () => requestAlarms(alarmGroupId!),
    enabled: !!alarmGroupId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch a single alarm
 */
export const useAlarm = (alarmId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: ALARM_QUERY_KEY(alarmId || ''),
    queryFn: () => requestAlarm(alarmId!),
    enabled: !!alarmId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to create an alarm
 */
export const useCreateAlarm = (
  alarmGroupId?: string,
): UseMutationResult<any, Error, { alarm: ICreateAlarmDTO; readme?: string }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ alarm, readme }: { alarm: ICreateAlarmDTO; readme?: string }) => createAlarmRequest(alarm, readme),
    onSuccess: () => {
      // Invalidate alarms list
      if (alarmGroupId) {
        void queryClient.invalidateQueries({ queryKey: ALARMS_QUERY_KEY(alarmGroupId) });
      }
      notification.success({
        message: 'Alarm Created',
      });
    },
    onError: (error: any) => {
      console.error('Error creating alarm', error);
      notification.error({
        message: error.message || 'Error creating alarm',
      });
    },
  });
};

/**
 * Hook to update an alarm
 */
export const useUpdateAlarm = (alarmId?: string, alarmGroupId?: string): UseMutationResult<any, Error, IUpdateAlarmDTO> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alarmData: IUpdateAlarmDTO) => updateAlarmRequest(alarmId!, alarmData),
    onSuccess: () => {
      // Invalidate the specific alarm query
      if (alarmId) {
        void queryClient.invalidateQueries({ queryKey: ALARM_QUERY_KEY(alarmId) });
      }
      // Invalidate the alarms list
      if (alarmGroupId) {
        void queryClient.invalidateQueries({ queryKey: ALARMS_QUERY_KEY(alarmGroupId) });
      }
      notification.success({
        message: 'Alarm Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating alarm', error);
      notification.error({
        message: error.message || 'Error updating alarm',
      });
    },
  });
};

/**
 * Hook to update alarm readme
 * If alarmId is provided, it will be used as the default
 * Otherwise, you can pass { alarmId, readme } to mutateAsync
 */
export const useUpdateAlarmReadme = (
  alarmId?: string,
  alarmGroupId?: string,
): UseMutationResult<any, Error, string | { alarmId: string; readme: string }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: string | { alarmId: string; readme: string }) => {
      // Support both old signature (just readme string) and new signature (object with alarmId)
      if (typeof params === 'string') {
        return updateAlarmReadmeRequest(alarmId!, params);
      } else {
        return updateAlarmReadmeRequest(params.alarmId, params.readme);
      }
    },
    onSuccess: (_, params) => {
      // Determine which alarmId was used
      const usedAlarmId = typeof params === 'string' ? alarmId : params.alarmId;

      // Invalidate the specific alarm query
      if (usedAlarmId) {
        void queryClient.invalidateQueries({ queryKey: ALARM_QUERY_KEY(usedAlarmId) });
      }
      // Invalidate the alarms list
      if (alarmGroupId) {
        void queryClient.invalidateQueries({ queryKey: ALARMS_QUERY_KEY(alarmGroupId) });
      }
      notification.success({
        message: 'Alarm Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating alarm readme', error);
      notification.error({
        message: error.message || 'Error updating alarm readme',
      });
    },
  });
};

/**
 * Hook to delete an alarm
 */
export const useDeleteAlarm = (alarmGroupId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alarmId: string) => deleteAlarmRequest(alarmId),
    onSuccess: () => {
      // Invalidate alarms list
      if (alarmGroupId) {
        void queryClient.invalidateQueries({ queryKey: ALARMS_QUERY_KEY(alarmGroupId) });
      }
      notification.success({
        message: 'Alarm Deleted',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting alarm', error);
      notification.error({
        message: error.message || 'Error deleting alarm',
      });
    },
  });
};
